import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import OLMap from "ol/Map";
import View from "ol/View";
import TileLayer from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import XYZ from "ol/source/XYZ";
import Feature from "ol/Feature";
import Point from "ol/geom/Point";
import { fromLonLat, toLonLat } from "ol/proj";
import { Style, Icon } from "ol/style";
import Overlay from "ol/Overlay";
import type { Extent } from "ol/extent";
import { useAppDispatch, useAppSelector, selectDrone } from "@/store";
import { DronePopup } from "./DronePopup";
import type { DroneState } from "@/types/fleet";
import { Box, IconButton, Text, Flex } from "@chakra-ui/react";
import { Layers, Sun, Moon, CloudMoon } from "lucide-react";
import type { ViewportBounds, FleetDroneState } from "@/hooks/useFleetSocket";

const DRONE_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
  <path d="M12 2L12 8"/>
  <path d="M12 16L12 22"/>
  <path d="M2 12L8 12"/>
  <path d="M16 12L22 12"/>
  <circle cx="12" cy="12" r="4"/>
  <circle cx="4" cy="4" r="2"/>
  <circle cx="20" cy="4" r="2"/>
  <circle cx="4" cy="20" r="2"/>
  <circle cx="20" cy="20" r="2"/>
</svg>`;

type MapStyle = "light" | "gray" | "dark";

const MAP_STYLES: Record<
  MapStyle,
  { url: string; label: string; icon: typeof Sun }
> = {
  light: {
    url: "https://{a-d}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png",
    label: "Light",
    icon: Sun,
  },
  gray: {
    url: "https://{a-d}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
    label: "Moon",
    icon: CloudMoon,
  },
  dark: {
    url: "https://{a-d}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
    label: "Dark",
    icon: Moon,
  },
};

function createDroneIcon(color: string, rotation: number): Style {
  const svg = DRONE_ICON_SVG.replace("currentColor", color);
  const encodedSvg = encodeURIComponent(svg);

  return new Style({
    image: new Icon({
      src: `data:image/svg+xml,${encodedSvg}`,
      scale: 1,
      rotation: (rotation * Math.PI) / 180,
      anchor: [0.5, 0.5],
    }),
  });
}

function getDroneColor(drone: FleetDroneState): string {
  if (!drone.connected) return "#dc2626"; // Red - disconnected
  if (drone.battery < 20) {
    return "#f59e0b"; // Yellow - low battery
  }
  return "#22c55e"; // Green - healthy
}

// Convert OpenLayers extent to ViewportBounds
function extentToViewportBounds(extent: Extent): ViewportBounds {
  const [minX, minY, maxX, maxY] = extent;
  const sw = toLonLat([minX, minY]);
  const ne = toLonLat([maxX, maxY]);

  return {
    sw: { lat: sw[1], lng: sw[0] },
    ne: { lat: ne[1], lng: ne[0] },
  };
}

export interface FleetMapProps {
  drones: Map<string, FleetDroneState>;
  setViewport: (bounds: ViewportBounds) => void;
}

export function FleetMap({ drones, setViewport }: FleetMapProps) {
  const dispatch = useAppDispatch();
  const selectedDroneId = useAppSelector((state) => state.fleet.selectedDroneId);

  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<OLMap | null>(null);
  const tileLayerRef = useRef<TileLayer<XYZ> | null>(null);
  const vectorSource = useRef<VectorSource>(new VectorSource());
  const overlayRef = useRef<Overlay | null>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const prevSelectedDroneId = useRef<string | null>(null);

  const [hoveredDroneId, setHoveredDroneId] = useState<string | null>(null);
  const [mapStyle, setMapStyle] = useState<MapStyle>(() => {
    const saved = localStorage.getItem("mapStyle") as MapStyle;
    return saved && saved in MAP_STYLES ? saved : "light";
  });
  const [showStyleMenu, setShowStyleMenu] = useState(false);

  // Convert fleet drones to DroneState for popup compatibility
  const dronesMap = useMemo(() => {
    const map = new globalThis.Map<string, DroneState>();

    for (const [droneId, drone] of drones) {
      map.set(droneId, {
        id: drone.id,
        name: drone.id,
        model: "Unknown",
        connected: drone.connected,
        telemetry: {
          timestamp: drone.lastUpdate,
          droneId: drone.id,
          position: {
            latitude: drone.lat,
            longitude: drone.lng,
            altitude: drone.alt,
          },
          attitude: {
            roll: 0,
            pitch: 0,
            yaw: drone.heading,
          },
          battery: {
            voltage: 0,
            percentage: drone.battery,
          },
        },
        lastUpdate: drone.lastUpdate,
        record: {
          droneId: drone.id,
          status: "approved",
          metadata: {},
          createdAt: 0,
          updatedAt: drone.lastUpdate,
        },
      });
    }

    return map;
  }, [drones]);

  const hoveredDrone = hoveredDroneId
    ? dronesMap.get(hoveredDroneId) ?? null
    : null;

  // Handler for drone selection
  const handleSelectDrone = useCallback(
    (droneId: string | null) => {
      dispatch(selectDrone(droneId));
    },
    [dispatch]
  );

  // Update viewport when map view changes
  const updateViewport = useCallback(() => {
    const map = mapInstance.current;
    if (!map) return;

    const extent = map.getView().calculateExtent(map.getSize());
    const bounds = extentToViewportBounds(extent);
    setViewport(bounds);
  }, [setViewport]);

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;

    const overlay = new Overlay({
      element: popupRef.current!,
      positioning: "bottom-center",
      offset: [0, -20],
      stopEvent: false,
    });
    overlayRef.current = overlay;

    const tileLayer = new TileLayer({
      source: new XYZ({
        url: MAP_STYLES[mapStyle].url,
        attributions:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
      }),
    });
    tileLayerRef.current = tileLayer;

    const map = new OLMap({
      target: mapRef.current,
      layers: [
        tileLayer,
        new VectorLayer({
          source: vectorSource.current,
        }),
      ],
      overlays: [overlay],
      view: new View({
        center: fromLonLat([14.8059, 56.879]), // Växjö default
        zoom: 12,
      }),
    });

    mapInstance.current = map;

    // Send initial viewport after map is ready
    map.once("rendercomplete", () => {
      updateViewport();
    });

    // Update viewport on view changes (debounced)
    let viewportTimeout: number | undefined;
    map.getView().on("change", () => {
      if (viewportTimeout) clearTimeout(viewportTimeout);
      viewportTimeout = window.setTimeout(updateViewport, 200);
    });

    return () => {
      if (viewportTimeout) clearTimeout(viewportTimeout);
      map.setTarget(undefined);
      mapInstance.current = null;
    };
  }, [mapStyle, updateViewport]);

  // Set up click and hover handlers
  useEffect(() => {
    const map = mapInstance.current;
    const overlay = overlayRef.current;
    if (!map || !overlay) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleClick = (event: any) => {
      const feature = map.forEachFeatureAtPixel(event.pixel, (f) => f);
      if (feature) {
        const droneId = feature.get("droneId") as string;
        handleSelectDrone(droneId);
      } else {
        handleSelectDrone(null);
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handlePointerMove = (event: any) => {
      const feature = map.forEachFeatureAtPixel(event.pixel, (f) => f);
      if (feature) {
        const droneId = feature.get("droneId") as string;
        setHoveredDroneId(droneId);
        const geometry = feature.getGeometry() as Point;
        const coords = geometry.getCoordinates();
        overlay.setPosition(coords);
      } else {
        setHoveredDroneId(null);
        overlay.setPosition(undefined);
      }

      // Change cursor
      const target = map.getTargetElement();
      target.style.cursor = feature ? "pointer" : "";
    };

    map.on("click" as "singleclick", handleClick);
    map.on("pointermove" as "singleclick", handlePointerMove);

    return () => {
      map.un("click" as "singleclick", handleClick);
      map.un("pointermove" as "singleclick", handlePointerMove);
    };
  }, [handleSelectDrone]);

  // Update tile layer when style changes
  useEffect(() => {
    if (!tileLayerRef.current) return;

    tileLayerRef.current.setSource(
      new XYZ({
        url: MAP_STYLES[mapStyle].url,
        attributions:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
      })
    );
    localStorage.setItem("mapStyle", mapStyle);
  }, [mapStyle]);

  // Update drone markers from fleet socket data
  useEffect(() => {
    const source = vectorSource.current;
    source.clear();

    const features: Feature[] = [];

    for (const [droneId, drone] of drones) {
      const feature = new Feature({
        geometry: new Point(fromLonLat([drone.lng, drone.lat])),
        droneId,
      });

      const color = getDroneColor(drone);
      feature.setStyle(createDroneIcon(color, drone.heading));

      features.push(feature);
    }

    source.addFeatures(features);
  }, [drones]);

  // Center map on drone only when selection changes (not on every telemetry update)
  useEffect(() => {
    if (
      selectedDroneId &&
      selectedDroneId !== prevSelectedDroneId.current &&
      mapInstance.current
    ) {
      const selectedDrone = drones.get(selectedDroneId);
      if (selectedDrone) {
        mapInstance.current.getView().animate({
          center: fromLonLat([selectedDrone.lng, selectedDrone.lat]),
          duration: 500,
        });
      }
    }
    prevSelectedDroneId.current = selectedDroneId;
  }, [selectedDroneId, drones]);

  const handleStyleChange = (style: MapStyle) => {
    setMapStyle(style);
    setShowStyleMenu(false);
  };

  return (
    <Box position="relative" h="full" w="full">
      <Box ref={mapRef} h="full" w="full" />
      <Box ref={popupRef} pointerEvents="none">
        {hoveredDrone && <DronePopup drone={hoveredDrone} />}
      </Box>

      {/* Map style selector */}
      <Box position="absolute" top="3" right="3" zIndex="10">
        <Box position="relative">
          <IconButton
            variant="solid"
            aria-label="Map style"
            onClick={() => setShowStyleMenu(!showStyleMenu)}
            shadow="md"
          >
            <Layers style={{ height: 16, width: 16 }} />
          </IconButton>

          {showStyleMenu && (
            <Box
              position="absolute"
              top="full"
              right="0"
              mt="2"
              bg="bg.subtle"
              borderWidth="1px"
              borderColor="border"
              borderRadius="md"
              shadow="lg"
              overflow="hidden"
            >
              {(Object.keys(MAP_STYLES) as MapStyle[]).map((style) => {
                const { label, icon: IconComponent } = MAP_STYLES[style];
                return (
                  <Flex
                    as="button"
                    key={style}
                    onClick={() => handleStyleChange(style)}
                    align="center"
                    gap="2"
                    w="full"
                    px="3"
                    py="2"
                    fontSize="sm"
                    bg={mapStyle === style ? "bg.emphasized" : "transparent"}
                    _hover={{ bg: "bg.emphasized" }}
                    transition="colors 0.2s"
                  >
                    <IconComponent style={{ height: 16, width: 16 }} />
                    <Text>{label}</Text>
                  </Flex>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
